import React from 'react';
import VirtualizedSelect from 'react-virtualized-select';

import Group from '@lightspeed/cirrus-group';
import { AutocompleteProps } from '@lightspeed/flame/Autocomplete';
import { Flag } from '@lightspeed/flame/Flag';
import cn from 'classnames';

import { getCountryList } from './utils';

const flagMap: { [key: string]: string } = {
  HL: 'SH-HL',
};

const baseClass = 'gui-def-autocomplete cr-autocomplete';
const autocompleteClass = 'cr-autocomplete-country';
const countries = getCountryList();

type CountryOption = {
  countryName: string;
  countryCode: string;
  customCountryName?: string;
};

const CountryOptionRenderer: React.FC<AutocompleteProps> = ({
  focusedOption,
  focusOption,
  key,
  option,
  selectValue,
  style,
  valueArray,
}) => {
  const { countryCode, countryName } = option;
  const classNames = cn('Select-option', `${baseClass}__option`, {
    'is-focused': option === focusedOption,
    'is-selected': valueArray.indexOf(option) >= 0,
  });

  return (
    // We follow the rule, but it still fails, see:
    // https://github.com/evcohen/eslint-plugin-jsx-a11y/blob/master/docs/rules/no-static-element-interactions.md#case-this-element-is-not-a-button-link-menuitem-etc-it-is-catching-bubbled-events-from-elements-that-it-contains
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div
      key={key}
      className={classNames}
      style={style}
      onClick={() => selectValue(option)}
      onMouseEnter={() => focusOption(option)}
      role="presentation"
    >
      <Group>
        <Flag code={flagMap[countryCode] || countryCode} />
        <var>{countryName}</var>
      </Group>
    </div>
  );
};

class CountryAutocomplete extends React.Component<AutocompleteProps> {
  selectRef: any;
  useCustomCountryName: boolean;

  constructor(props: CountryOption) {
    super(props);

    const { countryCode, countryName } = this.props;

    this.useCustomCountryName = false;

    if (countryCode && countryName) {
      const countryWithCustomNameIndex = countries.findIndex(
        (country) => country.countryCode === countryCode && country.countryName !== countryName
      );

      if (countryWithCustomNameIndex !== -1) {
        // @ts-ignore
        countries[countryWithCustomNameIndex].customCountryName = countryName;
        this.useCustomCountryName = true;
      }
    }

    this.selectRef = React.createRef();

    this.handleFlagClick = this.handleFlagClick.bind(this);
  }

  handleFlagClick() {
    this.selectRef.current.focus();
  }

  render() {
    const {
      onChange = () => {},
      onInputChange,
      countryName,
      countryCode,
      placeholder = 'Select a Country',
      className,
      ...rest
    } = this.props;

    let selectClass = cn(baseClass, autocompleteClass, className);
    let customPlaceholder = placeholder;

    // @Note: React-select does not allow for setting a default outside of options
    // Instead trick the placeholder to display the custom countryName
    if (!countryCode && countryName) {
      customPlaceholder = countryName;
      selectClass = cn(selectClass, 'cr-autocomplete--zz-placeholder');
    }

    return (
      <div className={`${autocompleteClass}__wrapper`} data-test="country-autocomplete">
        {/* @ts-ignore */}
        <VirtualizedSelect
          className={selectClass}
          {...rest}
          valueRenderer={(option) => (
            <var>{this.useCustomCountryName ? option.customCountryName : option.countryName}</var>
          )}
          labelKey="countryName"
          valueKey="countryCode"
          placeholder={customPlaceholder}
          optionRenderer={CountryOptionRenderer}
          optionHeight={36}
          options={countries}
          onBlurResetsInput={false}
          onCloseResetsInput={false}
          noResultsText={null}
          value={countryCode}
          ref={this.selectRef}
          onChange={(option) => {
            this.useCustomCountryName = false;
            // @ts-ignore
            onChange(option);
          }}
          onInputChange={onInputChange}
          onFocus={(e) => {
            const TOP_BAR_HEIGHT = 66;
            const isTouch = 'ontouchstart' in document.documentElement;

            // On touch devices, we want to scroll to the top of the input when focusing,
            // else in most cases the keyboard will overlay the autocomplete menu.
            if (isTouch) {
              const elementPosition = e.target.getBoundingClientRect();
              window.scroll({
                top: elementPosition.top + window.scrollY - TOP_BAR_HEIGHT,
              });
            }
          }}
        />

        <div // eslint-disable-line
          className={`${baseClass}__flag`}
          onClick={this.handleFlagClick}
          role="button"
          tabIndex={-1}
        >
          <span>
            <Flag code={countryCode ? flagMap[countryCode] || countryCode : 'ZZ'} />
          </span>
        </div>
      </div>
    );
  }
}

export default CountryAutocomplete;
